import { useEffect, useMemo, useRef, useState } from 'react';
import type { Cardapio, StoreProduto, StorePizza } from '../../lib/api';
import { apiBase } from '../../lib/config';
import { formatBRL } from '../../lib/format';
import { regraDoFrete } from '../../lib/order';
import { accentVar, categoriaEmoji, monograma, totalItens } from '../../lib/catalog';
import { pizzaUtilizavel, tamanhoInicialDoSabor, menorPrecoDoSabor, precoSaborNoTamanho } from '../../lib/pizzaPreco';
import { rotulosAlergenos } from '../../lib/alergenos';
import { whatsappBase } from '../../lib/whatsapp';
import { useBag } from '../../hooks/useBag';
import { EmptyState, NoResultsState } from '../States';
import { QuickView } from './QuickView';
import { BagDrawer } from './BagDrawer';
import { Checkout } from './Checkout';
import { MesaOrderConfirm } from '../mesa/MesaOrderConfirm';
import type { MesaEntrada } from '../../lib/api';

// Cardápio do cliente — réplica ESTRUTURAL da loja online do Vendeon (mesmo layout/fluxo), com a NOSSA
// identidade (creme + serifa + brasa, sem o azul/lima do Vendeon). De cima a baixo: cabeçalho com busca +
// sacola, herói escuro com card/emoji, chips de categoria que FILTRAM a grade (1º "Todos"), trilhas curadas
// (Mais vendidos + Ofertas), grade única com toolbar (categoria · contagem · ordenar) e "Carregar mais",
// rodapé — mais os overlays (quick-view, sacola, checkout). Sem foto: glyph/emoji por categoria.

type SortId = 'relevance' | 'price-asc' | 'price-desc';

const SORTS: { id: SortId; label: string }[] = [
  { id: 'relevance', label: 'Relevância' },
  { id: 'price-asc', label: 'Menor preço' },
  { id: 'price-desc', label: 'Maior preço' },
];

const TODOS = 'Todos';
const PAGE = 24; // janela inicial da grade; "Carregar mais" soma +PAGE (espelha o Vendeon)

// Menor preço COBRÁVEL do produto (QA bloco12 #2): para um produto COM variações, o backend trata a `preco`
// base como PLACEHOLDER (nunca cobrada — pedir sem variação é recusado), então o menor preço real é o da
// menor variação. Sem variações, a base é o piso real (adicionais são extras opcionais).
function precoBase(p: StoreProduto): number {
  if (p.variacoes.length > 0) {
    return Math.min(...p.variacoes.map((v) => v.preco));
  }
  return p.preco;
}

/**
 * Pizza (dono 08/09): cada SABOR do config vira um CARD próprio já selecionável na grade — título = nome do
 * sabor, preço = preço do sabor no tamanho GRANDE (o de menor `ordem`) —, mais um card "Monte sua pizza" que
 * abre a montagem VAZIA (comportamento anterior). `GridEntry` separa o que o card EXIBE (`display` — nome/
 * preço sintéticos) do produto REAL que a montagem (QuickView) recebe (`original` — sempre o produto de pizza
 * completo, com tamanhos/sabores/bordas). `saborInicialId` diz à montagem qual sabor já vem marcado (null =
 * abre vazia). Produto sem `pizza` vira 1 entrada só, idêntica ao card de sempre (display === original).
 */
interface GridEntry {
  key: string;
  display: StoreProduto;
  original: StoreProduto;
  saborInicialId: string | null;
  /** F2-M2 — preço/config não resolvível (lojista inativou tamanhos/sabores/preços de uma pizza que continua
   *  ativa): o card é tratado como INDISPONÍVEL — nunca "a partir de R$ 0,00" como se fosse de graça. */
  indisponivel: boolean;
  /** QA-28 C-01/A-04 — preço do card de SABOR: valor + NOME do tamanho em que a montagem vai abrir, mais o
   *  "a partir de" quando o mesmo sabor sai mais barato em outro tamanho. `undefined` fora do card de sabor. */
  saborPreco?: SaborPrecoCard;
}

/** QA-28 A-04 — o que o card de um SABOR precisa imprimir: o preço do tamanho com que a montagem ABRE (para
 *  vitrine e montagem nunca divergirem) + o tamanho mais barato desse sabor, quando existir outro. */
interface SaborPrecoCard {
  preco: number;
  tamanhoNome: string;
  /** Tamanho/preço mais baratos do MESMO sabor (só quando forem menores que o inicial). */
  menor: { preco: number; tamanhoNome: string } | null;
}

/**
 * QA-28 C-01/A-04 — preço do card de um SABOR. É o preço do sabor no tamanho em que a montagem REALMENTE vai
 * abrir (`tamanhoInicialDoSabor`: 1º tamanho por `ordem` em que ESSE sabor tem preço), não mais o do tamanho
 * inicial genérico com fallback no `Math.max` dos preços do sabor. Aquele fallback publicava o preço de um
 * tamanho que não era o aberto e — pior — dava preço a um sabor SEM preço no tamanho inicial, deixando o card
 * habilitado para abrir uma montagem travada (checkbox marcado E desabilitado, "Adicionar" morto).
 * `null` = sabor sem preço em nenhum tamanho existente → card INDISPONÍVEL (nunca R$ 0,00).
 */
function precoSaborCard(pizza: StorePizza, sabor: StorePizza['sabores'][number]): SaborPrecoCard | null {
  if (pizza.tamanhos.length === 0 || sabor.precos.length === 0) return null;
  const tamanho = tamanhoInicialDoSabor(pizza, sabor.id);
  if (!tamanho) return null;
  const preco = precoSaborNoTamanho(pizza, sabor.id, tamanho.id);
  if (preco == null) return null;
  const menor = menorPrecoDoSabor(pizza, sabor.id);
  return {
    preco,
    tamanhoNome: tamanho.nome,
    menor: menor != null && menor.preco < preco ? { preco: menor.preco, tamanhoNome: menor.tamanho.nome } : null,
  };
}

/** Menor preço entre TODOS os sabores/tamanhos — o "a partir de" do card "Monte sua pizza". F2-M2 — `null`
 *  quando a pizza não tem NENHUMA combinação utilizável (`pizzaUtilizavel`, lib/pizzaPreco.ts): tamanhos
 *  vazios, sabores vazios ou nenhum sabor com preço casado a um tamanho existente. */
function menorPrecoPizza(pizza: StorePizza): number | null {
  if (!pizzaUtilizavel(pizza)) return null;
  const todos = pizza.sabores.flatMap((s) => s.precos.map((pr) => pr.preco));
  return todos.length > 0 ? Math.min(...todos) : null;
}

function expandirProduto(p: StoreProduto): GridEntry[] {
  const pizza = p.pizza;
  if (!pizza) {
    return [{ key: p.produtoId || p.sku || p.nome, display: p, original: p, saborInicialId: null, indisponivel: false }];
  }
  const entradasSabor: GridEntry[] = pizza.sabores.map((s) => {
    const saborPreco = precoSaborCard(pizza, s);
    return {
      key: `${p.produtoId}::sabor::${s.id}`,
      // variacoes:[] evita o "a partir de" genérico das variações — o card do sabor imprime o próprio
      // rótulo (tamanho + preço + "a partir de" do tamanho mais barato) via `saborPreco`.
      display: { ...p, nome: s.nome, descricao: s.descricao, preco: saborPreco?.preco ?? 0, variacoes: [] },
      original: p,
      saborInicialId: s.id,
      indisponivel: saborPreco == null,
      ...(saborPreco ? { saborPreco } : {}),
    };
  });
  const apartir = menorPrecoPizza(pizza);
  const montarSuaPizza: GridEntry = {
    key: `${p.produtoId}::montar`,
    display: {
      ...p,
      nome: 'Monte sua pizza',
      descricao: null,
      // 1 "variação" sintética (preço = o menor de todos) reaproveita o rótulo "a partir de" do ProductCard.
      // Sem preço resolvível, fica variacoes:[] — o ProductCard usa `indisponivel` para o rótulo (nunca 0).
      variacoes: apartir != null ? [{ id: '_pizza_a_partir', nome: '', preco: apartir, porcaoDescricao: null, rendimentoPessoas: null }] : [],
    },
    original: p,
    saborInicialId: null,
    indisponivel: apartir == null,
  };
  return [...entradasSabor, montarSuaPizza];
}

/** A7 — quantos PRODUTOS REAIS (produtoId distintos) uma lista de entradas representa. Uma pizza vira N
 *  entradas (1 por sabor + "Monte sua pizza"), todas com o MESMO `produtoId` — contar entradas ("89") em vez
 *  de produtos ("44") faz o rótulo textual mentir sobre quantos itens existem no cardápio. A paginação em si
 *  (janela `visible`, "Carregar mais") continua por ENTRADAS — é a exibição do número que muda. */
function produtosDistintos(entradas: GridEntry[]): number {
  return new Set(entradas.map((e) => e.original.produtoId)).size;
}

function ordenar(entradas: GridEntry[], sort: SortId): GridEntry[] {
  if (sort === 'relevance') return entradas;
  const arr = [...entradas];
  // Ordena pelo menor preço COBRÁVEL (não pela base placeholder) — senão "Menor preço" rankeava produtos
  // com variações por um valor que o cliente não paga.
  arr.sort((a, b) => (sort === 'price-asc' ? precoBase(a.display) - precoBase(b.display) : precoBase(b.display) - precoBase(a.display)));
  return arr;
}

/** Produto precisa de configuração (abre o quick-view em vez de adicionar direto). Pizza SEMPRE precisa —
 *  mesmo com um sabor pré-marcado (card de sabor), o cliente ainda escolhe tamanho/2º sabor/borda. */
function precisaConfig(p: StoreProduto): boolean {
  return p.variacoes.length > 0 || p.adicionalGrupos.length > 0 || !!p.pizza;
}

/** Casa o termo de busca com nome/descrição (acento/caixa-insensível). */
function casa(p: StoreProduto, q: string): boolean {
  if (!q) return true;
  const alvo = (p.nome + ' ' + (p.descricao ?? '') + ' ' + p.categoria)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  return alvo.includes(q);
}

// Card de produto — MÓDULO-LEVEL (identidade de tipo ESTÁVEL): definido dentro de StoreView, o React o
// tratava como um tipo novo a cada render e REMONTAVA todos os cards a cada tecla/interação — recriando cada
// <img> e piscando os produtos sem foto (QA L1). Aqui recebe tudo por props, sem capturar closure do pai.
function ProductCard({
  p,
  slug,
  accent,
  fav,
  onFav,
  onOpen,
  onAdd,
  best,
  offer,
  showFav = true,
  pizzaAPartirDe,
  indisponivel = false,
  saborPreco,
}: {
  p: StoreProduto;
  slug: string;
  accent: string;
  fav: boolean;
  onFav: (id: string) => void;
  onOpen: (p: StoreProduto) => void;
  onAdd: (p: StoreProduto) => void;
  best: boolean;
  offer: boolean;
  /** M7 — um card de SABOR (pizza expandida) não tem favorito PRÓPRIO: favoritar 1 sabor acendia o ♥ em
   *  TODOS os cards daquela pizza (mesmo `produtoId`). Sabor não exibe o botão; "Monte sua pizza" e produtos
   *  comuns (default) continuam mostrando. */
  showFav?: boolean;
  /** A5 — quando o card representa uma pizza INTEIRA vinda "crua" (trilhos Mais vendidos/Ofertas, que
   *  curam pelo produto e não passam pelo `expandirProduto` da grade), o preço exibido é "a partir de" o
   *  menor preço entre sabores/tamanhos — não o `p.preco` placeholder (nunca cobrado). `undefined` preserva o
   *  comportamento normal (variação/preço fixo) — usado pelos cards de sabor e produtos sem pizza. Ignorado
   *  quando `indisponivel` é true. */
  pizzaAPartirDe?: number;
  /** F2-M2 — preço/config não resolvível (pizza sem tamanho/sabor utilizável): o card mostra um rótulo claro
   *  ("Indisponível no momento") em vez de "a partir de R$ 0,00"/R$ 0,00, e a ação de adicionar fica
   *  desabilitada — "Ver detalhes" continua funcionando (explica em vez de travar mudo). */
  indisponivel?: boolean;
  /** QA-28 A-04 — card de SABOR de pizza: o preço vem acompanhado do NOME do tamanho a que pertence (o mesmo
   *  com que a montagem abre) e, quando o sabor sai mais barato em outro tamanho, de um "a partir de". Sem
   *  isto o card publicava o preço do maior tamanho como se fosse "o" preço da pizza. */
  saborPreco?: SaborPrecoCard;
}): JSX.Element {
  const sem = p.esgotado;
  // Fase 7.1 — defensivo (produtos podem chegar sem os campos novos, ex.: mocks/versões antigas).
  const alergContem = p.alergenosContem ?? [];
  // Selo (mesma prioridade do Vendeon): Esgotado > Indisponível > Oferta > Mais vendido.
  const selo = sem
    ? { cls: 'out', txt: 'Esgotado' }
    : indisponivel
      ? { cls: 'out', txt: 'Indisponível' }
      : offer
        ? { cls: 'offer', txt: 'Oferta' }
        : best
          ? { cls: 'best', txt: 'Mais vendido' }
          : null;
  return (
    <article
      className={'loja-card' + (sem ? ' loja-card-esgotado' : '')}
      style={{ ['--accent' as string]: accent }}
    >
      <div className="loja-card-media">
        {showFav && (
          <button
            className={'loja-fav' + (fav ? ' is-on' : '')}
            aria-pressed={fav}
            aria-label={(fav ? 'Remover ' : 'Favoritar ') + p.nome}
            onClick={() => onFav(p.produtoId)}
          >
            {fav ? '♥' : '♡'}
          </button>
        )}
        {selo && <span className={'loja-tag loja-tag--' + selo.cls}>{selo.txt}</span>}
        <button className="loja-card-open" onClick={() => onOpen(p)} aria-label={`Ver detalhes de ${p.nome}`}>
          <span className="loja-card-emoji" aria-hidden="true">
            {categoriaEmoji(p.categoria || p.nome)}
          </span>
          <span className="loja-card-glyph" aria-hidden="true">
            {monograma(p.nome)}
          </span>
          {/* Foto do produto (servida pelo backend por slug+id). Cobre o emoji quando existe; se falhar, o
              onError a esconde e o emoji reaparece. A src leva apiBase() (o cardápio roda em outra origem).
              QA L5 — só pede a imagem quando `temImagem` (sinal explícito do backend): sem isso, TODO produto
              sem foto gerava 404 em massa (rate-limit 120/min por IP → 429 sumindo fotos reais). Antes o gate
              era imagemVersao>0, que escondia fotos ANTERIORES à V200 (versão 0 sem backfill). ?v= é cache-buster. */}
          {p.produtoId && p.temImagem && (
            <img
              className="loja-card-photo"
              src={`${apiBase()}/api/v1/public/${encodeURIComponent(slug)}/produtos/${p.produtoId}/imagem?v=${p.imagemVersao ?? 0}`}
              alt=""
              loading="lazy"
              // QA — a foto é servida por rota com rate-limit por IP (429 sob Wi-Fi compartilhado/NAT ou
              // cardápio grande); sem retry, um 429 passageiro escondia a foto PARA SEMPRE na sessão (mesmo
              // depois da janela de rate-limit liberar), sobrando só o emoji genérico. `dataset.retry` marca
              // o elemento pra permitir só UMA nova tentativa (evita loop infinito); o cache-buster `r=`
              // força o navegador a não repetir a resposta 429 do cache.
              onError={(e) => {
                const img = e.currentTarget;
                if (img.dataset.retry === '1') {
                  img.style.display = 'none';
                  return;
                }
                img.dataset.retry = '1';
                const original = img.src;
                window.setTimeout(() => {
                  img.src = original + (original.includes('?') ? '&' : '?') + 'r=' + Date.now();
                }, 4000);
              }}
            />
          )}
          <span className="loja-card-peek" aria-hidden="true">
            Ver detalhes
          </span>
        </button>
      </div>
      <div className="loja-card-body">
        {p.categoria && <span className="loja-card-cat">{p.categoria}</span>}
        <h3 className="loja-card-name" title={p.nome}>
          {p.nome}
        </h3>
        {/* Fase 7.1 — selo de dieta (vegano implica vegetariano; mostra o mais forte) + aviso de alérgeno. */}
        {(p.vegano || p.vegetariano) && (
          <div className="loja-card-diet">
            <span className={'loja-diet ' + (p.vegano ? 'loja-diet--vegano' : 'loja-diet--veg')}>
              {p.vegano ? 'Vegano' : 'Vegetariano'}
            </span>
          </div>
        )}
        {alergContem.length > 0 && (
          <p className="loja-card-alerg" title={'Contém: ' + rotulosAlergenos(alergContem).join(', ')}>
            ⚠ Contém {rotulosAlergenos(alergContem).join(', ')}
          </p>
        )}
        <div className="loja-card-price">
          {indisponivel ? (
            // F2-M2 — nunca "R$ 0,00": um rótulo claro em vez de fingir um preço/promoção que não existe.
            // QA-28 M-01/B-02 — status NUNCA só por cor: o ⚠ acompanha o texto (a classe própria dá o peso
            // visual de aviso, que antes não existia no CSS e saía em 16px de tinta cheia).
            <span className="loja-card-price-from loja-card-price-indispo">
              <span aria-hidden="true">⚠</span> Indisponível no momento
            </span>
          ) : saborPreco != null ? (
            // QA-28 A-04 — preço do SABOR com o tamanho a que pertence (o mesmo que a montagem abre) e, se
            // houver, o tamanho mais barato do mesmo sabor: a vitrine deixa de parecer preço único.
            <>
              <span className="loja-card-price-from">{saborPreco.tamanhoNome}</span>
              <b>{formatBRL(saborPreco.preco)}</b>
              {saborPreco.menor && (
                <small className="loja-card-price-alt">
                  {saborPreco.menor.tamanhoNome} a partir de {formatBRL(saborPreco.menor.preco)}
                </small>
              )}
            </>
          ) : pizzaAPartirDe != null ? (
            <span className="loja-card-price-from">
              a partir de <b>{formatBRL(pizzaAPartirDe)}</b>
            </span>
          ) : p.variacoes.length > 0 ? (
            <span className="loja-card-price-from">
              a partir de <b>{formatBRL(precoBase(p))}</b>
            </span>
          ) : (
            <b>{formatBRL(p.preco)}</b>
          )}
        </div>
        <button
          className="loja-btn loja-add"
          onClick={() => onAdd(p)}
          disabled={sem || indisponivel}
          aria-disabled={sem || indisponivel}
        >
          {sem ? 'Esgotado' : indisponivel ? 'Indisponível' : precisaConfig(p) ? 'Escolher opções' : 'Adicionar à sacola'}
        </button>
      </div>
    </article>
  );
}

// Trilha horizontal curada (Mais vendidos / Ofertas) — também módulo-level, pela mesma razão do ProductCard.
function Rail({
  title,
  sub,
  list,
  onVerTudo,
  slug,
  favSet,
  onFav,
  onOpen,
  onAdd,
  bestSet,
  offerSet,
  accentDe,
}: {
  title: string;
  sub: string;
  list: StoreProduto[];
  onVerTudo: () => void;
  slug: string;
  favSet: Set<string>;
  onFav: (id: string) => void;
  onOpen: (p: StoreProduto) => void;
  onAdd: (p: StoreProduto) => void;
  bestSet: Set<string>;
  offerSet: Set<string>;
  accentDe: (nome: string) => string;
}): JSX.Element | null {
  if (list.length === 0) return null;
  return (
    <section className="loja-rail-section">
      <div className="loja-rail-head">
        <div>
          <h2>{title}</h2>
          <p>{sub}</p>
        </div>
        <button className="loja-rail-link" onClick={onVerTudo}>
          Ver tudo →
        </button>
      </div>
      <div className="loja-rail" role="list">
        {list.map((p) => {
          // A5 — os trilhos curam pelo PRODUTO (não passam pelo expandirProduto/GridEntry da grade), então
          // uma pizza curada chega aqui "crua" (variacoes:[] , preco placeholder). "a partir de" o menor
          // preço entre sabores/tamanhos, igual ao card "Monte sua pizza". F2-M2 — `null` (pizza sem
          // tamanho/sabor utilizável) vira INDISPONÍVEL, nunca "a partir de R$ 0,00".
          const pizzaAPartirDe = p.pizza ? menorPrecoPizza(p.pizza) : undefined;
          return (
            <div role="listitem" key={`rail-${p.produtoId || p.nome}`}>
              <ProductCard
                p={p}
                slug={slug}
                accent={accentDe(p.categoria)}
                fav={favSet.has(p.produtoId)}
                onFav={onFav}
                onOpen={onOpen}
                onAdd={onAdd}
                best={bestSet.has(p.produtoId)}
                offer={offerSet.has(p.produtoId)}
                pizzaAPartirDe={pizzaAPartirDe ?? undefined}
                indisponivel={!!p.pizza && pizzaAPartirDe == null}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

export interface StoreMesaMode {
  codigo: string;
  token: string;
  entrada: MesaEntrada;
  /** QA lote12 #8 — chamado quando o backend recusa o pedido por sessão revogada/expirada (401 →
   *  CardapioError 'unavailable'): a MesaPage limpa a sessão morta e reexibe o formulário de entrar. */
  onSessionExpired?: () => void;
  /** O servidor recusou um item que o catálogo carregado ainda mostra (QA 31/10/2026): recarrega o cardápio para a
   *  sacola marcar o item e travar o envio até ele sair. */
  onCatalogoDesatualizado?: () => void;
}

export function StoreView({
  cardapio,
  slug,
  mesa,
}: {
  cardapio: Cardapio;
  slug: string;
  mesa?: StoreMesaMode;
}) {
  const [termo, setTermo] = useState('');
  const [sort, setSort] = useState<SortId>('relevance');
  const [cat, setCat] = useState<string>(TODOS);
  const [visible, setVisible] = useState(PAGE);
  const [fav, setFav] = useState<Set<string>>(() => new Set());
  // `quick` guarda o produto REAL da montagem + o sabor pré-marcado (card de sabor de pizza); null =
  // fechado. `saborInicialId` é sempre null fora do fluxo de pizza (produto comum, "Monte sua pizza",
  // trilhos curados).
  const [quick, setQuick] = useState<{ produto: StoreProduto; saborInicialId: string | null } | null>(null);
  const [bagOpen, setBagOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [mesaOrderOpen, setMesaOrderOpen] = useState(false);
  // QA L5 #9 — persistência da sacola (sobrevive a reload/pull-to-refresh). Mesa: sessionStorage (escopo da
  // sessão da mesa, some ao fechar a aba). Delivery: localStorage com TTL de 12h (volta mais tarde, mas não
  // ressuscita carrinho obsoleto).
  // QA — a chave da sacola de mesa usa mesa.entrada.sessaoId (não mesa.codigo): o código é o QR FIXO da mesa
  // física, igual para todo mundo que senta ali. Chaveando pelo código, a sacola sobrevivia à troca de cliente
  // e o próximo via a sacola abandonada do anterior. sessaoId é único por participante/sessão → a sacola morre
  // junto com a sessão (e o mount de StoreView, que só ocorre com uma entrada nova — ver MesaPage).
  const bag = useBag(
    mesa
      ? { area: 'session', key: `${slug}:mesa:${mesa.entrada.sessaoId}` }
      : { area: 'local', key: `${slug}:delivery`, ttlMs: 12 * 60 * 60 * 1000 }
  );
  const catalogRef = useRef<HTMLDivElement | null>(null);
  const tituloGradeRef = useRef<HTMLHeadingElement | null>(null);

  // QA L3 — trava o scroll da PÁGINA de fundo enquanto há overlay aberto (sacola/quick-view/checkout/mesa):
  // sem isto, arrastar o dedo no backdrop ou rolar além do fim encadeava o scroll no catálogo e ele perdia a
  // posição ao fechar. O overscroll-behavior:contain (CSS) complementa nos contêineres internos.
  useEffect(() => {
    const algumAberto = bagOpen || Boolean(quick) || checkoutOpen || mesaOrderOpen;
    if (!algumAberto) return;
    const html = document.documentElement;
    const overflowAnterior = html.style.overflow;
    const paddingAnterior = html.style.paddingRight;
    // QA L4 — compensa a calha da barra de rolagem ao travar (senão o conteúdo salta ~15px no desktop com
    // scrollbar clássica, visível através do backdrop translúcido). No mobile (scrollbar overlay) a calha é 0.
    const calha = window.innerWidth - html.clientWidth;
    html.style.overflow = 'hidden';
    if (calha > 0) html.style.paddingRight = calha + 'px';
    return () => {
      html.style.overflow = overflowAnterior;
      html.style.paddingRight = paddingAnterior;
    };
  }, [bagOpen, quick, checkoutOpen, mesaOrderOpen]);

  const shopName = cardapio.tenantNome || 'Nosso cardápio';
  // CAPA/banner: quando a loja tem capa (imagem 1600×500 já com a arte do lojista), ela é exibida como um
  // BANNER LIMPO e inteiro (proporção 3.2:1, SEM escurecimento e SEM texto por cima — Opção A do dono) e a
  // identidade da loja fica numa faixa logo ABAIXO. ?v=<versao> é o cache-buster. Sem capa → herói terracota.
  const capaUrl =
    cardapio.capaVersao != null
      ? `${apiBase()}/api/v1/public/${encodeURIComponent(slug)}/capa?v=${cardapio.capaVersao}`
      : null;
  // Identidade da loja (saudação + selos), reaproveitada nos dois formatos de herói (com/sem capa).
  const heroEyebrow = mesa
    ? `Mesa ${mesa.entrada.mesaNumero} · Olá, ${mesa.entrada.participanteNome}`
    : 'Cardápio';
  const heroTrust = (
    <div className="loja-hero-trust">
      <span className="loja-pill">
        <span aria-hidden="true">{mesa ? '✓' : '⌂'}</span>{' '}
        {mesa ? 'Pedido direto na mesa' : 'Retirada e entrega'}
      </span>
      <span className="loja-pill">
        <span aria-hidden="true">◷</span> Feito na hora
      </span>
      <span className="loja-pill">
        <span aria-hidden="true">{mesa ? '⌁' : '○'}</span>{' '}
        {mesa ? 'Pagamento no caixa' : 'Atendimento fácil'}
      </span>
    </div>
  );
  const total = useMemo(() => totalItens(cardapio), [cardapio]);

  // Índice produto por id (resolve destaques/ofertas escolhidos pelo lojista) + índice de categoria (accent).
  const produtoPorId = useMemo(() => {
    const m = new Map<string, StoreProduto>();
    for (const c of cardapio.categorias) for (const p of c.produtos) m.set(p.produtoId, p);
    return m;
  }, [cardapio]);
  // QA-28 B-06 — ids indisponíveis segundo o catálogo ATUAL: esgotados + os que saíram do cardápio (a linha
  // da sacola guarda o produto de quando foi adicionado e não sabe disso sozinha).
  const indisponiveisIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of bag.bag) {
      const atual = produtoPorId.get(item.produto.produtoId);
      if (!atual || atual.esgotado) ids.add(item.produto.produtoId);
    }
    return ids;
  }, [bag.bag, produtoPorId]);
  const catIndex = useMemo(() => {
    const m = new Map<string, number>();
    cardapio.categorias.forEach((c, i) => m.set(c.nome, i));
    return m;
  }, [cardapio]);
  const accentDe = (nome: string): string => accentVar(catIndex.get(nome) ?? 0);

  // "Mais vendidos": LISTA CURADA que o lojista definiu no painel (GET público), na ordem escolhida — mesmo
  // modelo de "Ofertas". SEM curadoria (ou com todos os curados indisponíveis), `destaques` fica vazio e o
  // trilho NÃO aparece (Rail retorna null com lista vazia). Dono 20/08: nada de amostra automática — ela
  // mascarava o cardápio como se houvesse destaques parametrizados quando não havia.
  const destaques = useMemo(
    () =>
      (cardapio.destaques ?? [])
        .map((id) => produtoPorId.get(id))
        .filter((p): p is StoreProduto => Boolean(p)),
    [cardapio.destaques, produtoPorId]
  );
  const destaquesTitulo = 'Mais vendidos';
  const destaquesSub = 'Os queridinhos da casa';
  // Ofertas: LISTA CURADA que o lojista definiu no painel (GET público), na ordem escolhida — mesmo modelo
  // dos destaques. Sem ofertas definidas, o trilho não aparece.
  const ofertas = useMemo(
    () =>
      (cardapio.ofertas ?? [])
        .map((id) => produtoPorId.get(id))
        .filter((p): p is StoreProduto => Boolean(p)),
    [cardapio.ofertas, produtoPorId]
  );
  // Conjuntos p/ o SELO do card (Mais vendido / Oferta), como no Vendeon.
  const bestSet = useMemo(() => new Set(cardapio.destaques ?? []), [cardapio.destaques]);
  const offerSet = useMemo(() => new Set(cardapio.ofertas ?? []), [cardapio.ofertas]);

  // Chips: "Todos" + categorias COM produtos (na ordem do cardápio), SEM nomes repetidos. QA L1 — categorias
  // ativas mas VAZIAS não viram chip (senão cairiam no "busca sem resultado" com aspas vazias). QA L5 — o
  // backend tolera categorias HOMÔNIMAS (Categoria.nome sem unique); dedupe por nome evita key React duplicada
  // e dois chips idênticos (o filtro por nome já agrupa as homônimas sob o mesmo chip).
  const chips = useMemo(() => {
    // QA bloco14 #5 — uma categoria REAL homônima ao sentinel "Todos" colidia com o 1º chip (key React
    // duplicada) e não era isolável (cat === 'Todos' cai no "mostrar tudo"). Como no dedupe das homônimas
    // (QA L5), dobra-se sob o chip "Todos" (all): seus produtos seguem visíveis na visão geral e na busca.
    const nomes = cardapio.categorias
      .filter((c) => c.produtos.length > 0)
      .map((c) => c.nome)
      .filter((n) => n !== TODOS);
    return [TODOS, ...Array.from(new Set(nomes))];
  }, [cardapio]);

  // Grade: produtos da categoria (chip) EXPANDIDOS em entradas de exibição (pizza → 1 por sabor + "Monte sua
  // pizza"; demais produtos → 1 entrada, igual a sempre), filtradas por busca (o título de CADA entrada —
  // inclusive o nome do sabor — entra no casamento) e ordenadas.
  const entradasDaCategoria = useMemo(
    () =>
      cardapio.categorias
        .filter((c) => cat === TODOS || c.nome === cat)
        .flatMap((c) => c.produtos)
        .flatMap(expandirProduto),
    [cardapio, cat]
  );
  const filtrados = useMemo(() => {
    const q = termo
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
      .toLowerCase();
    const base = entradasDaCategoria.filter((e) => casa(e.display, q));
    return ordenar(base, sort);
  }, [entradasDaCategoria, termo, sort]);
  const visiveis = filtrados.slice(0, visible);
  // A7 — contagem para EXIBIÇÃO (rótulo textual) em produtos reais, não em entradas da grade.
  const totalProdutos = useMemo(() => produtosDistintos(filtrados), [filtrados]);
  const visiveisProdutos = useMemo(() => produtosDistintos(visiveis), [visiveis]);
  // QA-28 A-03 — unidade REALMENTE paginada (entradas/cards da grade). Quando difere dos produtos reais
  // (pizza expandida em sabores), o rótulo mostra as duas coisas em vez de mentir sobre a grade.
  const totalOpcoes = filtrados.length;

  const cardapioVazio = total === 0;
  const buscaAtiva = termo.trim().length > 0;
  const semResultado = !cardapioVazio && filtrados.length === 0;
  // Trilhas só na visão geral (todos + sem busca), como o Vendeon. QA L1 — usa o termo APARADO: um termo só
  // com espaços não deve esconder as trilhas (a grade nem é filtrada nesse caso).
  const mostrarTrilhas = !cardapioVazio && cat === TODOS && !buscaAtiva;
  const tituloGrade = cat === TODOS ? 'Todos os produtos' : cat;

  function selecionarCat(nome: string) {
    setCat(nome);
    setVisible(PAGE);
    // Rola até a grade (sem animar container grande — scrollIntoView é seguro).
    const el = catalogRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function scrollToGrid() {
    const el = catalogRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function onBuscar(v: string) {
    setTermo(v);
    setVisible(PAGE);
  }
  function toggleFav(id: string) {
    setFav((f) => {
      const n = new Set(f);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  /** Abre a montagem (QuickView) para o produto REAL `p`, opcionalmente com um sabor de pizza pré-marcado. */
  function openProduct(p: StoreProduto, saborInicialId: string | null = null) {
    setQuick({ produto: p, saborInicialId });
  }

  function quickAdd(p: StoreProduto) {
    // Esgotado (estoque): não entra no carrinho — guarda defensiva além do `disabled` do botão.
    if (p.esgotado) return;
    if (precisaConfig(p)) {
      openProduct(p);
      return;
    }
    bag.add({ produto: p, variacao: null, adicionais: [], qty: 1 });
    setBagOpen(true);
  }

  /** Card de SABOR/"Monte sua pizza" na grade: abre sempre a montagem com o `original` + o sabor da entrada
   *  (guarda o esgotado — mesma regra do quickAdd). "Ver detalhes" (onOpen) não é gated por estoque, como
   *  no card comum: só o "Adicionar"/"Escolher opções" (onAdd) é. */
  function abrirEntry(entry: GridEntry) {
    openProduct(entry.original, entry.saborInicialId);
  }
  function adicionarEntry(entry: GridEntry) {
    // F2-M2 — rede de segurança além do `disabled` do botão: um card indisponível (pizza sem tamanho/sabor
    // utilizável) nunca abre a montagem nem adiciona à sacola.
    if (entry.indisponivel) return;
    if (entry.original.pizza) {
      if (entry.original.esgotado) return;
      abrirEntry(entry);
      return;
    }
    quickAdd(entry.original);
  }

  function startCheckout() {
    if (bag.bag.length === 0) return;
    // Rede de segurança além do botão travado: com item indisponível na sacola, não abre o envio (QA 31/10/2026).
    if (bag.bag.some((i) => i.produto.esgotado || indisponiveisIds.has(i.produto.produtoId))) return;
    setBagOpen(false);
    if (mesa) setMesaOrderOpen(true);
    else setCheckoutOpen(true);
  }

  return (
    <div className="loja">
      <header className="loja-header">
        <div className="loja-brand">
          <div className="loja-logo" aria-hidden="true">
            {monograma(shopName)}
          </div>
          <div className="loja-brand-title">
            <strong>{shopName}</strong>
            <small>Cardápio digital</small>
          </div>
        </div>
        {!cardapioVazio && (
          <div className="loja-search loja-search--header">
            <span className="loja-search-ico" aria-hidden="true">
              🔎
            </span>
            <input
              type="search"
              inputMode="search"
              placeholder="Buscar no cardápio…"
              aria-label="Buscar itens no cardápio"
              value={termo}
              onChange={(e) => onBuscar(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') scrollToGrid();
              }}
            />
            {termo && (
              <button
                type="button"
                className="loja-search-clear"
                aria-label="Limpar busca"
                onClick={() => onBuscar('')}
              >
                ×
              </button>
            )}
          </div>
        )}
        <button
          className="loja-bagbtn"
          onClick={() => setBagOpen(true)}
          aria-label={`Abrir sacola com ${bag.count} ${bag.count === 1 ? 'item' : 'itens'}`}
        >
          <span aria-hidden="true">🧺</span>
          <span className="loja-bagbtn-txt">Sacola</span>
          {bag.count > 0 && <span className="loja-bagbtn-count">{bag.count}</span>}
        </button>
      </header>

      {capaUrl ? (
        // COM CAPA (Opção A do dono): o banner 1600×500 é exibido INTEIRO e LIMPO — sem escurecimento e sem
        // texto por cima (nada de "poluído/efumaçado") — e a identidade da loja fica numa FAIXA logo abaixo.
        <section className="loja-capa-section" aria-label={`Apresentação de ${shopName}`}>
          <div
            className="loja-capa"
            style={{ backgroundImage: `url("${capaUrl}")` }}
            role="img"
            aria-label={`Capa de ${shopName}`}
          />
          <div className="loja-capa-identity">
            <div className="loja-capa-identity__main">
              <span className="loja-eyebrow">{heroEyebrow}</span>
              <h1 className="loja-capa-name">{shopName}</h1>
            </div>
            {heroTrust}
          </div>
        </section>
      ) : (
        // SEM CAPA: herói terracota padrão (texto + arte do emoji) sobre o card escuro.
        <section className="loja-hero">
          <div className="loja-hero-content">
            <span className="loja-eyebrow">{heroEyebrow}</span>
            <h1 className="loja-hero-title">{shopName}</h1>
            <p className="loja-hero-desc">
              {mesa
                ? 'Escolha seus itens e envie direto para a cozinha. O pagamento é feito no caixa quando quiser.'
                : 'Peça pelo cardápio digital e escolha retirar no local ou receber em casa.'}
            </p>
            {heroTrust}
          </div>
          <div className="loja-hero-art" aria-hidden="true">
            <span className="loja-hero-glow" />
            <span className="loja-hero-emoji">{mesa ? '🍽' : '🍔'}</span>
          </div>
        </section>
      )}

      {!cardapioVazio && (
        <nav className="loja-chipsbar" aria-label="Categorias do cardápio">
          {chips.map((nome) => (
            <button
              key={nome}
              type="button"
              className="loja-chip"
              style={nome === TODOS ? undefined : { ['--accent' as string]: accentDe(nome) }}
              aria-current={cat === nome ? 'true' : undefined}
              onClick={() => selecionarCat(nome)}
            >
              {nome}
            </button>
          ))}
        </nav>
      )}

      <main className="loja-main">
        {/* QA-28 M-09 — vazio SEMPRE com CTA: sem itens, o cliente ainda pode falar com a loja (quando ela
            tem WhatsApp) em vez de encarar um beco decorativo. */}
        {cardapioVazio && <EmptyState whatsappHref={whatsappBase(cardapio.whatsapp)} />}

        {!cardapioVazio && (
          <>
            {mostrarTrilhas && (
              <Rail
                title={destaquesTitulo}
                sub={destaquesSub}
                list={destaques}
                onVerTudo={scrollToGrid}
                slug={slug}
                favSet={fav}
                onFav={toggleFav}
                onOpen={openProduct}
                onAdd={quickAdd}
                bestSet={bestSet}
                offerSet={offerSet}
                accentDe={accentDe}
              />
            )}
            {mostrarTrilhas && (
              <Rail
                title="Ofertas"
                sub="Selecionadas pela casa"
                list={ofertas}
                onVerTudo={scrollToGrid}
                slug={slug}
                favSet={fav}
                onFav={toggleFav}
                onOpen={openProduct}
                onAdd={quickAdd}
                bestSet={bestSet}
                offerSet={offerSet}
                accentDe={accentDe}
              />
            )}

            <section className="loja-catalog" ref={catalogRef}>
              <div className="loja-toolbar">
                <div className="loja-toolbar-head">
                  {/* tabIndex=-1 + ref: alvo de foco quando o "Carregar mais" some no último clique (QA L3). */}
                  <h2 ref={tituloGradeRef} tabIndex={-1}>{tituloGrade}</h2>
                  {/* QA-28 A-03 — o rótulo tem de falar da MESMA unidade que a grade pagina. Uma pizza vira
                      N entradas (1 por sabor + "Monte sua pizza") do mesmo produtoId: dizer só "1 produto"
                      sobre 46 cards (e "1 de 1 produtos" ao lado de um "Carregar mais" ativo) desmentia a
                      tela. Agora: produtos reais E, quando divergem, as OPÇÕES exibidas/paginadas. */}
                  <span className="loja-toolbar-count" aria-live="polite">
                    {totalProdutos} {totalProdutos === 1 ? 'produto' : 'produtos'}
                    {totalOpcoes !== totalProdutos && ` · ${totalOpcoes} opções`}
                  </span>
                </div>
                <label className="loja-sort">
                  <span className="loja-sort-label">Ordenar por</span>
                  <select value={sort} onChange={(e) => setSort(e.target.value as SortId)}>
                    {SORTS.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {semResultado ? (
                buscaAtiva ? (
                  // QA-28 M-09 — CTA explícito: antes o único jeito de sair do "nada encontrado" era achar o
                  // "×" minúsculo dentro do campo de busca.
                  <NoResultsState termo={termo} onClear={() => onBuscar('')} />
                ) : (
                  // Categoria sem itens (não é uma busca): mensagem própria, sem aspas vazias. QA L1.
                  <div className="loja-empty-cat">Esta categoria ainda não tem itens no cardápio.</div>
                )
              ) : (
                <>
                  <div className="loja-grid">
                    {visiveis.map((entry) => (
                      <ProductCard
                        key={entry.key}
                        p={entry.display}
                        slug={slug}
                        accent={accentDe(entry.display.categoria)}
                        fav={fav.has(entry.original.produtoId)}
                        onFav={toggleFav}
                        onOpen={() => abrirEntry(entry)}
                        onAdd={() => adicionarEntry(entry)}
                        best={bestSet.has(entry.original.produtoId)}
                        offer={offerSet.has(entry.original.produtoId)}
                        // M7 — card de SABOR (saborInicialId != null) não tem favorito próprio: favoritar 1
                        // sabor acendia o ♥ em TODOS os cards da mesma pizza (mesmo produtoId). Só "Monte sua
                        // pizza" e produtos comuns (saborInicialId null) mostram o botão.
                        showFav={entry.saborInicialId == null}
                        // F2-M2 — preço/config não resolvível (ver expandirProduto): rótulo "Indisponível",
                        // nunca "R$ 0,00".
                        indisponivel={entry.indisponivel}
                        // QA-28 A-04 — card de sabor: preço COM o tamanho (o mesmo que a montagem abre).
                        saborPreco={entry.saborPreco}
                      />
                    ))}
                  </div>
                  {visiveis.length < filtrados.length && (
                    <div className="loja-loadmore">
                      <button
                        className="loja-btn loja-btn--primary loja-btn--lg"
                        onClick={() => {
                          const eraUltima = visible + PAGE >= filtrados.length;
                          setVisible((v) => v + PAGE);
                          // QA L3 — no último clique o botão desmonta e o foco cairia no <body>; reposiciona
                          // no título da grade para não perder o lugar (WCAG 2.4.3).
                          if (eraUltima) requestAnimationFrame(() => tituloGradeRef.current?.focus());
                        }}
                      >
                        Carregar mais produtos
                      </button>
                      {/* QA-28 A-03 — a paginação é por ENTRADAS (opções): o contador ao lado do botão fala
                          a unidade paginada, senão dizia "1 de 1 produtos" com o botão ainda clicável. */}
                      <span className="loja-loadmore-count" aria-live="polite">
                        {totalOpcoes !== totalProdutos
                          ? `${visiveis.length} de ${totalOpcoes} opções`
                          : `${visiveisProdutos} de ${totalProdutos} produtos`}
                      </span>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </main>

      <footer className="loja-footer">
        <div className="loja-footer-grid">
          <div className="loja-footer-col loja-footer-brand">
            <div className="loja-logo" aria-hidden="true">
              {monograma(shopName)}
            </div>
            <strong>{shopName}</strong>
            <p>{mesa ? 'Peça pelo celular e receba direto na sua mesa.' : 'Peça pelo cardápio digital e escolha receber em casa ou retirar no local.'}</p>
          </div>
          {!mesa && whatsappBase(cardapio.whatsapp) && (
            <div className="loja-footer-col">
              <h4>Atendimento</h4>
              <a
                className="loja-footer-wa"
                // Mesmo helper canônico do handoff pós-pedido (Checkout): prefixa o DDI 55 num telefone local
                // e trata WhatsApp salvo como link. O `wa.me/${replace(/\D/g)}` inline gerava número sem DDI
                // (destino errado) e destruía links — divergindo do resto do app.
                href={whatsappBase(cardapio.whatsapp) ?? undefined}
                target="_blank"
                rel="noreferrer"
              >
                <span aria-hidden="true">💬</span> Falar no WhatsApp
              </a>
            </div>
          )}
          <div className="loja-footer-col">
            <h4>{mesa ? 'Na sua mesa' : 'Entrega e retirada'}</h4>
            <p>{mesa ? 'Pedido enviado direto à cozinha' : 'Retirada gratuita no local'}</p>
            {/* QA 31/10/2026 — pelas mesmas regras do checkout (zona, rua, taxa padrão); "combinada" só sem nenhuma. */}
            <p>
              {mesa
                ? 'Atendimento direto na mesa'
                : regraDoFrete(cardapio.zonasEntrega, cardapio.ruasEntrega, cardapio.taxaEntregaPadrao) ??
                  'Entrega combinada no atendimento'}
            </p>
          </div>
          <div className="loja-footer-col">
            <h4>Pagamento</h4>
            <div className="loja-pays">
              <span>Pix</span>
              <span>Cartão</span>
              <span>{mesa ? 'No caixa' : 'Na entrega'}</span>
            </div>
            <p className="loja-secure">
              <span aria-hidden="true">🔒</span> Pedido feito com segurança
            </p>
          </div>
        </div>
        <div className="loja-footer-foot">
          <span>
            © {new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', year: 'numeric' }).format(new Date())} {shopName}
          </span>
          <span className="loja-powered">
            Cardápio com tecnologia <b>Vitto</b>
          </span>
        </div>
      </footer>

      {bag.count > 0 && !bagOpen && !quick && !checkoutOpen && !mesaOrderOpen && (
        <button className="loja-floating-bag" onClick={() => setBagOpen(true)}>
          <span className="loja-floating-bag-count">{bag.count}</span>
          <strong>Ver pedido</strong>
          <b>{formatBRL(bag.subtotal)}</b>
        </button>
      )}

      {quick && (
        <QuickView
          produto={quick.produto}
          saborInicialId={quick.saborInicialId}
          slug={slug}
          onClose={() => setQuick(null)}
          onAdd={(input) => {
            if (quick?.produto.esgotado) return; // esgotado (estoque) não entra no carrinho — guarda defensiva
            bag.add(input);
            setQuick(null);
            setBagOpen(true);
          }}
        />
      )}

      {bagOpen && (
        <BagDrawer
          bag={bag.bag}
          count={bag.count}
          subtotal={bag.subtotal}
          onClose={() => setBagOpen(false)}
          onChangeQty={bag.changeQty}
          onRemove={bag.remove}
          onCheckout={startCheckout}
          // QA-28 B-06 — sinaliza na PRÓPRIA sacola o que o catálogo atual já diz indisponível (esgotado ou
          // removido do cardápio): a linha guarda o snapshot do produto de quando entrou.
          indisponiveisIds={indisponiveisIds}
        />
      )}

      {checkoutOpen && (
        <Checkout
          slug={slug}
          shopName={shopName}
          whatsapp={cardapio.whatsapp}
          bag={bag.bag}
          subtotal={bag.subtotal}
          zonas={cardapio.zonasEntrega}
          ruas={cardapio.ruasEntrega ?? []}
          taxaPadrao={cardapio.taxaEntregaPadrao ?? null}
          fusoHorario={cardapio.fusoHorario}
          onClose={() => setCheckoutOpen(false)}
          onOrderPlaced={bag.clear}
          // QA-28 A-02 — o checkout detecta o item esgotado/fora do cardápio; a sacola é DAQUI, então a
          // remoção também: sem isso o cliente ficava preso no aviso "revise a sacola" sem ação possível.
          onRemoveItens={(keys) => keys.forEach((k) => bag.remove(k))}
        />
      )}

      {mesaOrderOpen && mesa && (
        <MesaOrderConfirm
          codigo={mesa.codigo}
          token={mesa.token}
          bag={bag.bag}
          subtotal={bag.subtotal}
          mesaNumero={mesa.entrada.mesaNumero}
          taxaConsumoPercentual={mesa.entrada.taxaConsumoPercentual}
          onClose={() => setMesaOrderOpen(false)}
          onPlaced={() => {
            bag.clear();
          }}
          onSessionExpired={mesa.onSessionExpired}
          onItemIndisponivel={() => {
            // Item recusado pelo servidor: recarrega o catálogo e volta à sacola, onde ele aparece marcado com
            // "Remover" e o envio fica travado até sair.
            mesa.onCatalogoDesatualizado?.();
            setMesaOrderOpen(false);
            setBagOpen(true);
          }}
        />
      )}
    </div>
  );
}
